import { useEffect, useState } from 'react';
import Header from './component/header';
import Loader from './component/Loader';
import HomePage from './pages/HomePage';
import InnerPage from './pages/InnerPage';
import MarketplacePage from './pages/MarketplacePage';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AuthPage from './pages/AuthPage';
import {
  ADMIN_DASHBOARD_SECTIONS,
  ROUTE_PATHS,
  USER_DASHBOARD_SECTIONS,
  getDashboardSection,
  isAdminDashboardRoute,
  isMarketplaceRoute,
  isUserDashboardRoute,
  navigateTo,
  normalizeRoute,
  shouldHidePublicHeader,
} from './app/routes';
import { resolveRouteAccess } from './app/routeGuards';
import { getStoredUser, getToken } from './services/authStorage';
import { THEME_DARK, THEME_LIGHT, getStoredTheme, saveStoredTheme } from './services/themeStorage';
import './App.css';

function App() {
  const [route, setRoute] = useState(() => normalizeRoute());
  const [showLoader, setShowLoader] = useState(() => normalizeRoute() === '/');
  const [theme, setTheme] = useState(getStoredTheme);
  const routeAccess = resolveRouteAccess(route, { token: getToken(), user: getStoredUser() });
  const routeIsMarketplace = isMarketplaceRoute(route);
  const hideHeader = shouldHidePublicHeader(route);

  const toggleTheme = () => {
    setTheme((currentTheme) => saveStoredTheme(currentTheme === THEME_LIGHT ? THEME_DARK : THEME_LIGHT));
  };

  useEffect(() => {
    const handleRouteChange = () => {
      const nextRoute = normalizeRoute();
      setRoute(nextRoute);
      window.scrollTo(0, 0);
      if (nextRoute !== '/') {
        setShowLoader(false);
      }
    };

    window.addEventListener('popstate', handleRouteChange);

    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  useEffect(() => {
    const handleInternalLink = (event) => {
      const link = event.target.closest?.('a[href]');

      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const url = new URL(link.href, window.location.origin);
      const isInternal = url.origin === window.location.origin;
      const isAppPath = isInternal && !url.hash && !link.hasAttribute('download');

      if (!isAppPath || link.target) {
        return;
      }

      event.preventDefault();
      window.history.pushState({}, '', `${url.pathname}${url.search}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    };

    document.addEventListener('click', handleInternalLink);

    return () => document.removeEventListener('click', handleInternalLink);
  }, []);

  useEffect(() => {
    if (routeAccess.redirectTo) {
      navigateTo(routeAccess.redirectTo);
    }
  }, [routeAccess.redirectTo]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  if (!routeAccess.authorized) {
    return <div className="app-shell" />;
  }

  return (
    <div className={`app-shell site-theme site-theme--${theme}`}>
      {showLoader && route === '/' ? (
        <Loader onComplete={() => setShowLoader(false)} />
      ) : (
        <>
          {hideHeader ? null : <Header currentRoute={route} theme={theme} onToggleTheme={toggleTheme} />}
          {isUserDashboardRoute(route) ? (
            <UserDashboard section={getDashboardSection(route, ROUTE_PATHS.dashboard, USER_DASHBOARD_SECTIONS)} />
          ) : isAdminDashboardRoute(route) ? (
            <AdminDashboard section={getDashboardSection(route, ROUTE_PATHS.admin, ADMIN_DASHBOARD_SECTIONS)} />
          ) : route === '/login' ? (
            <AuthPage mode="login" />
          ) : route === '/register' ? (
            <AuthPage mode="register" />
          ) : route === '/forgot-password' ? (
            <AuthPage mode="forgot-password" />
          ) : route === '/reset-password' ? (
            <AuthPage mode="reset-password" />
          ) : routeIsMarketplace ? (
            <MarketplacePage route={route} />
          ) : (
            <main>
              {route === '/' ? <HomePage /> : <InnerPage route={route} />}
            </main>
          )}
        </>
      )}
    </div>
  );
}

export default App;
